export class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');
      if (!(template instanceof HTMLTemplateElement)) return;

      const shadow = this.attachShadow({ mode: 'open' });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

export class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

export class OverflowMinimumEvent extends Event {
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = { minimumReached };
  }
}

export class ReflowEvent extends Event {
  constructor(lastVisibleElement) {
    super('reflow', { bubbles: true });
    this.detail = { lastVisibleElement };
  }
}

let headerUpdateQueued = false;
let headerUpdateFrame = null;

let lastHeaderHeight = -1;
let lastHeaderGroupHeight = -1;
let lastTransparentOffset = '';

function getTransparentOffset(header, headerGroup) {
  const hasHeaderSection = headerGroup.querySelector('.header-section');
  const isTransparent = header.hasAttribute('transparent');

  return (
    hasHeaderSection &&
    isTransparent &&
    !hasHeaderSection.nextElementSibling?.classList.contains('shopify-section')
  )
    ? '1'
    : '0';
}

function getElementHeight(el) {
  if (!(el instanceof HTMLElement)) return 0;

  return Math.round(el.getBoundingClientRect().height || 0);
}

export function updateAllHeaderCustomProperties() {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  if (!header || !headerGroup) return;

  if (headerUpdateFrame) {
    cancelAnimationFrame(headerUpdateFrame);
  }

  headerUpdateFrame = requestAnimationFrame(() => {
    headerUpdateFrame = null;

    const headerHeight = getElementHeight(header);
    let headerGroupHeight = 0;

    for (const el of headerGroup.children) {
      if (el === header || !(el instanceof HTMLElement)) continue;
      headerGroupHeight += getElementHeight(el);
    }

    const transparentOffset = getTransparentOffset(header, headerGroup);

    if (
      headerHeight === lastHeaderHeight &&
      headerGroupHeight === lastHeaderGroupHeight &&
      transparentOffset === lastTransparentOffset
    ) {
      return;
    }

    lastHeaderHeight = headerHeight;
    lastHeaderGroupHeight = headerGroupHeight;
    lastTransparentOffset = transparentOffset;

    const root = document.documentElement.style;
    root.setProperty('--header-height', `${headerHeight}px`);
    root.setProperty('--header-group-height', `${headerGroupHeight}px`);
    root.setProperty('--transparent-header-offset-boolean', transparentOffset);
  });
}

export function updateAllHeaderProperties() {
  updateAllHeaderCustomProperties();
}

function queueHeaderUpdate() {
  if (headerUpdateQueued) return;

  headerUpdateQueued = true;

  const run = () => {
    headerUpdateQueued = false;
    updateAllHeaderCustomProperties();
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(run, { timeout: 500 });
  } else {
    setTimeout(run, 120);
  }
}

const initCritical = () => {
  const headerGroup = document.querySelector('#header-group');
  if (!headerGroup) return;

  const observer = new ResizeObserver(() => {
    queueHeaderUpdate();
  });

  observer.observe(headerGroup);

  queueHeaderUpdate();

  window.addEventListener('orientationchange', queueHeaderUpdate, { passive: true });
  window.addEventListener('resize', queueHeaderUpdate, { passive: true });
};

if (document.readyState === 'complete') {
  initCritical();
} else {
  window.addEventListener('load', initCritical, { once: true });
}

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', queueHeaderUpdate);
  document.addEventListener('shopify:section:reorder', queueHeaderUpdate);
  document.addEventListener('shopify:section:select', queueHeaderUpdate);
}